import React, { useState, useEffect, useCallback } from "react";
import Header from "../Components/Header";
import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  runTransaction,
} from "firebase/firestore";
import { db } from "../firebase/config"; // Corrected import

import { getLevelStyling } from "../utils/roadmapStylingHelper";
import RoadmapLevelList from "../Components/RoadmapLevelList";
import RoadmapSessionsList from "../Components/RoadmapSessionsList";
import RoadmapLevelModal from "../Components/RoadmapLevelModal";
import RoadmapSessionModal from "../Components/RoadmapSessionModal";
import RoadmapManagementSkeleton from "../Skeleton/RoadmapManagementSkeleton";

const RoadmapManagement = ({ isSidebarOpen, setIsSidebarOpen }) => {
  const [levels, setLevels] = useState([]);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [loading, setLoading] = useState(true);

  // Consolidated state for Level modal logic
  const [showLevelModal, setShowLevelModal] = useState(false);
  const [editingLevel, setEditingLevel] = useState(null);

  // State for new/editing session
  const [newSessionData, setNewSessionData] = useState({
    title: "",
    description: "",
    type: "Technical",
    order: "",
    dueDate: "",
    estimatedHours: "",
  });

  // State to store the session currently being edited
  const [editingSession, setEditingSession] = useState(null);
  const [showAddSessionModal, setShowAddSessionModal] = useState(false);
  const [showEditSessionModal, setShowEditSessionModal] = useState(false);

  // Fetch all levels and their sessions
  const fetchRoadmapData = useCallback(async (updateSelectedLevelId = null) => {
    setLoading(true);
    try {
      const levelsCollectionRef = collection(db, "roadmapLevels");
      const q = query(levelsCollectionRef, orderBy("order"));
      const levelsSnapshot = await getDocs(q);
      const fetchedLevels = [];

      for (const levelDoc of levelsSnapshot.docs) {
        const levelData = { id: levelDoc.id, ...levelDoc.data() };
        const sessionsCollectionRef = collection(db, `roadmapLevels/${levelDoc.id}/roadmap_sessions`);
        const sessionsQ = query(sessionsCollectionRef, orderBy("order"));
        const sessionsSnapshot = await getDocs(sessionsQ);
        const sessions = sessionsSnapshot.docs.map(sessionDoc => ({
          id: sessionDoc.id,
          ...sessionDoc.data(),
        }));
        // Apply dynamic styling here for display in management view
        const dynamicStyles = getLevelStyling(levelData.order);
        fetchedLevels.push({ ...levelData, ...dynamicStyles, items: sessions });
      }
      setLevels(fetchedLevels);
      
      // Update selectedLevel with fresh data if updateSelectedLevelId is provided
      if (updateSelectedLevelId) {
        const updatedSelectedLevel = fetchedLevels.find(l => l.id === updateSelectedLevelId);
        if (updatedSelectedLevel) {
          setSelectedLevel(updatedSelectedLevel);
        }
      }
      
      setLoading(false);
      return fetchedLevels;
    } catch (error) {
      console.error("Error fetching roadmap data:", error);
      setLoading(false);
      return null;
    }
  }, []);

  useEffect(() => {
    fetchRoadmapData();
  }, [fetchRoadmapData]);

  // Handle Level Operations
  const handleSaveLevel = async (data) => {
    if (!data.name || !data.order) {
      console.error("Level name and order are required.");
      return;
    }
    setLoading(true);
    try {
      if (data.id) {
        const levelRef = doc(db, "roadmapLevels", data.id);
        await updateDoc(levelRef, {
          name: data.name,
          subtitle: data.subtitle,
          description: data.description,
          order: parseInt(data.order),
        });
        console.log("Level updated successfully!");
      } else {
        // This is a new level
        const maxOrder = levels.length > 0 ? Math.max(...levels.map(l => l.order || 0)) : 0;
        const finalOrder = parseInt(data.order) > maxOrder ? parseInt(data.order) : maxOrder + 1;

        await addDoc(collection(db, "roadmapLevels"), {
          name: data.name,
          subtitle: data.subtitle,
          description: data.description,
          order: finalOrder,
        });
        console.log("New Level added!");
      }

      setShowLevelModal(false);
      setEditingLevel(null);
      await fetchRoadmapData();
    } catch (error) {
      console.error("Error saving level:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAddLevelModal = () => {
    setEditingLevel(null); 
    setShowLevelModal(true);
  };

  const handleEditLevel = (level) => {
    setEditingLevel(level); 
    setShowLevelModal(true);
  };

  const handleDeleteLevel = async (levelId) => {
    if (!window.confirm("Are you sure you want to delete this level and all its sessions?")) {
      return;
    }
    setLoading(true);
    try {
      const levelRef = doc(db, "roadmapLevels", levelId);
      const sessionsCollectionRef = collection(db, `roadmapLevels/${levelId}/roadmap_sessions`);
      const sessionsSnapshot = await getDocs(sessionsCollectionRef);

      await runTransaction(db, async (transaction) => {
        sessionsSnapshot.docs.forEach((sessionDoc) => {
          transaction.delete(sessionDoc.ref);
        });
        transaction.delete(levelRef);
      });

      console.log("Level and its sessions deleted successfully!");
      setSelectedLevel(null);
      await fetchRoadmapData();
    } catch (error) {
      console.error("Error deleting level:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle Session Operations
  const handleAddSession = async (data) => {
    console.log("data ", data);

    if (!selectedLevel?.id || !data.title || !data.order) {
      console.error("Level must be selected, and session title/order are required.");
      return;
    }
    setLoading(true);
    try {
      const maxSessionOrder = selectedLevel.items.length > 0
        ? Math.max(...selectedLevel.items.map(s => s.order || 0))
        : 0;
      const finalSessionOrder = parseInt(data.order) > maxSessionOrder ? parseInt(data.order) : maxSessionOrder + 1;

      // Create session data object with only defined values
      const sessionData = {
        title: data.title,
        description: data.description || "",
        type: data.type || "Technical",
        order: finalSessionOrder,
      };

      // Only add optional fields if they have values
      if (data.dueDate) {
        sessionData.dueDate = data.dueDate;
      }
      if (data.estimatedHours) {
        sessionData.estimatedHours = parseInt(data.estimatedHours);
      }

      await addDoc(collection(db, `roadmapLevels/${selectedLevel.id}/roadmap_sessions`), sessionData);
      
      console.log("New Session added!");
      setShowAddSessionModal(false);
      setNewSessionData({
        title: "", 
        description: "", 
        type: "Technical", 
        order: "", 
        dueDate: "", 
        estimatedHours: ""
      });
      await fetchRoadmapData(selectedLevel.id); // Pass the selectedLevel ID
    } catch (error) {
      console.error("Error adding session:", error);
    } finally {
      setLoading(false);
    }
  };

  // New function to prepare for adding a session (auto-fills order)
  const handleAddSessionClick = () => {
    if (selectedLevel) {
      const maxOrder = selectedLevel.items.length > 0
        ? Math.max(...selectedLevel.items.map(s => s.order || 0))
        : 0;
      setNewSessionData(prev => ({
        ...prev,
        order: (maxOrder + 1).toString(), // Pre-fill with next available order
      }));
    }
    setShowAddSessionModal(true);
  };

  const handleEditSession = (session) => {
    setEditingSession(session);
    setNewSessionData({
      title: session.title,
      description: session.description,
      type: session.type || "Technical",
      order: session.order,
      dueDate: session.dueDate || "",
      estimatedHours: session.estimatedHours || "",
    });
    setShowEditSessionModal(true);
  };

  const handleUpdateSession = async (data) => {
    if (!selectedLevel?.id || !editingSession?.id || !data.title || !data.order) {
      console.error("Level ID, session ID, title, and order are required for update.");
      return;
    }
    setLoading(true);
    try {
      const sessionRef = doc(db, `roadmapLevels/${selectedLevel.id}/roadmap_sessions`, editingSession.id);
      
      // Create update data object with only defined values
      const updateData = {
        title: data.title,
        description: data.description || "",
        type: data.type || "Technical",
        order: parseInt(data.order),
      };

      // Only add optional fields if they have values
      if (data.dueDate) {
        updateData.dueDate = data.dueDate;
      }
      if (data.estimatedHours) {
        updateData.estimatedHours = parseInt(data.estimatedHours);
      }

      await updateDoc(sessionRef, updateData);
      
      console.log("Session updated successfully!");
      setShowEditSessionModal(false);
      setNewSessionData({
        title: "", 
        description: "", 
        type: "Technical", 
        order: "", 
        dueDate: "", 
        estimatedHours: ""
      });
      setEditingSession(null);
      await fetchRoadmapData(selectedLevel.id); // Pass the selectedLevel ID
    } catch (error) {
      console.error("Error updating session:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSession = async (sessionId) => {
    if (!window.confirm("Are you sure you want to delete this session?")) {
      return;
    }
    setLoading(true);
    try {
      await deleteDoc(doc(db, `roadmapLevels/${selectedLevel.id}/roadmap_sessions`, sessionId));
      console.log("Session deleted successfully!");
      await fetchRoadmapData(selectedLevel.id); // Pass the selectedLevel ID
    } catch (error) {
      console.error("Error deleting session:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
        <RoadmapManagementSkeleton />
    );
  }

  return (
    <div className="text-white">
      <Header
        PageName={"Roadmap Management"}
        isSidebarOpen={isSidebarOpen}
        setIsSidebarOpen={setIsSidebarOpen}
      />
      <div className="bg-slate-900 p-6 rounded-lg border border-slate-700 min-h-[calc(100vh-100px)]">
        {!selectedLevel ? (
          <RoadmapLevelList
            levels={levels}
            setSelectedLevel={setSelectedLevel}
            handleEditLevel={handleEditLevel}
            handleDeleteLevel={handleDeleteLevel}
            handleOpenAddLevelModal={handleOpenAddLevelModal} // Updated prop name
          />
        ) : (
          <RoadmapSessionsList
            selectedLevel={selectedLevel}
            setSelectedLevel={setSelectedLevel}
            handleEditSession={handleEditSession}
            handleDeleteSession={handleDeleteSession}
            setShowAddSessionModal={handleAddSessionClick} 
          />
        )}
      </div>

      {/* Single Level Modal for both Add and Edit */}
      <RoadmapLevelModal
        showModal={showLevelModal}
        onClose={() => {
          setShowLevelModal(false);
          setEditingLevel(null); // Resetting editing level on close
        }}
        onSubmit={handleSaveLevel}
        initialData={editingLevel}
      />

      <RoadmapSessionModal
        showModal={showAddSessionModal}
        onClose={() => {
          setShowAddSessionModal(false);
          setNewSessionData({ 
            title: "", 
            description: "", 
            type: "Technical", 
            order: "", 
            dueDate: "", 
            estimatedHours: "" 
          });
        }}
        onSubmit={handleAddSession}
        isEditMode={false}
        selectedLevelName={selectedLevel?.name}
        initialData={newSessionData}
      />

      <RoadmapSessionModal
        showModal={showEditSessionModal}
        onClose={() => {
          setShowEditSessionModal(false);
          setNewSessionData({ 
            title: "", 
            description: "", 
            type: "Technical", 
            order: "", 
            dueDate: "", 
            estimatedHours: "" 
          });
          setEditingSession(null);
        }}
        onSubmit={handleUpdateSession}
        initialData={editingSession}
        isEditMode={true}
        selectedLevelName={selectedLevel?.name}
      />
    </div>
  );
};

export default RoadmapManagement;